import React, { useState } from 'react';
import { FileText, Plus, History, BarChart3, Settings, Upload, Menu, X } from 'lucide-react';
import Dashboard from './components/Dashboard';
import InvoiceForm from './components/InvoiceForm';
import BillingHistory from './components/BillingHistory';
import Analytics from './components/Analytics';
import SettingsPage from './components/SettingsPage';
import ImportExport from './components/ImportExport';

function App() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'create', label: 'Create Invoice', icon: Plus },
    { id: 'history', label: 'Invoice History', icon: History },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'import-export', label: 'Import / Export', icon: Upload },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard onNavigate={setActiveTab} />;
      case 'create':
        return <InvoiceForm onSuccess={() => setActiveTab('history')} />;
      case 'history':
        return <BillingHistory />;
      case 'analytics':
        return <Analytics />;
      case 'import-export':
        return <ImportExport />;
      case 'settings':
        return <SettingsPage />;
      default:
        return <Dashboard onNavigate={setActiveTab} />;
    }
  };

  return (
    <div data-testid="app-container" className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside 
        data-testid="sidebar"
        className={`fixed lg:sticky top-0 left-0 z-40 h-screen bg-white border-r border-zinc-200 transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-0 lg:w-20'
        } overflow-hidden`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-zinc-200">
            <div className={`flex items-center gap-3 ${!sidebarOpen && 'lg:justify-center'}`}>
              <div className="w-10 h-10 bg-emerald-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-white" />
              </div>
              {sidebarOpen && (
                <div className="animate-fade-in">
                  <h1 className="text-lg font-bold text-zinc-900">E1 Invoice Pro</h1>
                  <p className="text-xs text-zinc-500">Invoice Management</p>
                </div>
              )}
            </div>
            <button
              data-testid="sidebar-toggle"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 hover:bg-zinc-100 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  data-testid={`nav-${item.id}`}
                  onClick={() => {
                    setActiveTab(item.id);
                    if (window.innerWidth < 1024) setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-emerald-50 text-emerald-700 shadow-sm'
                      : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900'
                  } ${!sidebarOpen && 'lg:justify-center lg:px-2'}`}
                >
                  <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-emerald-700' : ''}`} />
                  {sidebarOpen && <span className="animate-fade-in">{item.label}</span>}
                </button>
              );
            })}
          </nav>

          {/* Footer */}
          {sidebarOpen && (
            <div className="p-4 border-t border-zinc-200">
              <p className="text-xs text-zinc-400 text-center">Das Sales & Service</p>
            </div>
          )}
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 min-w-0">
        {/* Top Bar */}
        <header className="sticky top-0 z-20 h-16 bg-white border-b border-zinc-200 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-4">
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 hover:bg-zinc-100 rounded-lg"
            >
              <Menu className="w-5 h-5" />
            </button>
            <h2 className="text-lg lg:text-xl font-semibold text-zinc-900 capitalize">
              {navItems.find(i => i.id === activeTab)?.label || 'Dashboard'}
            </h2>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              data-testid="quick-create-btn"
              onClick={() => setActiveTab('create')}
              className="hidden sm:flex items-center gap-2 px-4 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 transition-colors font-medium text-sm"
            >
              <Plus className="w-4 h-4" />
              New Invoice
            </button>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 lg:p-6">
          {renderContent()}
        </div>
      </main>
    </div>
  );
}

export default App;
